import { useQuery } from "@tanstack/react-query";
import {
  LifecycleManagementAllEvents,
  ActivityClient,
  GetActivityRequest,
  DateRange,
  GetActivityResponse,
  ChangeHistoryActorEntity,
  ChangeHistoryEntry,
  LifecycleManagementAssignedToDto,
} from "@lib/ShiOneClient";
import { DateTime } from "luxon";
import { useApi } from "@utils/useApi";

interface UseLifecycleActivityApiReturn {
  isLoading: boolean;
  activityData: GetActivityResponse | undefined;
  actors: ChangeHistoryActorEntity[];
  changedKeys: string[];
  activities: ChangeHistoryEntry[];
  assignees: LifecycleManagementAssignedToDto[];
}

export const useLifecycleActivityApi = (
  selectedLifecycleEventData: LifecycleManagementAllEvents | undefined,
  dateFrom: DateTime | null,
  dateTo: DateTime | null
): UseLifecycleActivityApiReturn => {
  const api = useApi(ActivityClient);

  const { data, isLoading } = useQuery({
    queryKey: [
      "lifecycle-activity",
      selectedLifecycleEventData?.id,
      dateFrom?.toISO(),
      dateTo?.toISO(),
    ],
    enabled: !!selectedLifecycleEventData?.id,
    queryFn: async () => {
      const dateRange = new DateRange();
      if (dateFrom) {
        dateRange.start = dateFrom.toJSDate();
      }
      if (dateTo) {
        dateRange.end = dateTo.toJSDate();
      }

      return await api.getLifecycleActivity(
        new GetActivityRequest({
          id: selectedLifecycleEventData!.id,
          dateRange,
        })
      );
    },
    retry: false,
  });

  return {
    isLoading,
    activityData: data,
    actors: data?.history?.actors || [],
    changedKeys: data?.history?.changedKeys || [],
    activities: data?.history?.activity || [],
    assignees: data?.assignees || [],
  };
};
